import { useRouter } from 'next/router';
import { useReduxState } from 'hooks';
import { breakpoints } from 'styles';
import { getContentTypeColors } from 'lib/analytics';
import { useApiData } from 'lib/api';
import { LoadingIndicator } from 'components/common';
import { Box } from 'components/common/box';
import { StatItemWithIcon, StatList } from 'components/common/stats';
import { AnalyticsBarChart, AnalyticsAreaChart, AnalyticsPageFilters } from '.';

import type { IconProp } from 'components/common/icons/Icon';
import type { GetActivityAnalyticsRes } from 'shared-types/responses';
import { PageEmptyIndicator } from 'components/page';

const AnalyticsActivity = () => {
  const {
    messages,
    serverConfig: {
      hideAchievementsFromUI,
      hideCalendarEventsFromUI,
      hideCoursesFromUI,
      hidePathsFromUI,
      individualAchievementsEnabled,
      individualAchievementsDisplayForAdmins,
      platformCompetenciesOnly
    },
    theme,
    user
  } = useReduxState();

  const router = useRouter();

  const { data, loading } = useApiData<GetActivityAnalyticsRes>({
    onlyIf: Boolean(user?.id),
    url:
      '/admin/analytics/activity' +
      Object.keys(router.query)
        .map((key, i) => (i === 0 ? '?' : '&') + `${key}=${router.query[key]}`)
        .join('')
  });

  const colorsByActivityType = getContentTypeColors();

  const isUserSuperadmin = user?.roles.includes('superadmin');

  const achievementsVisible = !hideAchievementsFromUI;
  const calendarEventsVisible = !hideCalendarEventsFromUI;
  const coursesVisible = !hideCoursesFromUI;
  const pathsVisible = !hidePathsFromUI;

  const showIndividualAchievements =
    individualAchievementsEnabled &&
    achievementsVisible &&
    (isUserSuperadmin || individualAchievementsDisplayForAdmins);

  return (
    <>
      <AnalyticsPageFilters />

      {loading ? (
        <LoadingIndicator />
      ) : !data ? (
        <PageEmptyIndicator icon="chart-bar" title={messages.commonNoData} />
      ) : (
        <div className="grid">
          <div className="side">
            <div className="stats-completions">
              <Box
                padding={0}
                title={messages.analyticsActivityCompletionsByType}
              >
                <StatList
                  items={[
                    ...(coursesVisible
                      ? [
                          {
                            icon: 'book' as IconProp,
                            label: messages.analyticsActivityCompletionsCourses,
                            value:
                              data?.activities.numberOfNewCompletions.course
                          }
                        ]
                      : []),
                    ...(pathsVisible
                      ? [
                          {
                            icon: 'tasks' as IconProp,
                            label: messages.analyticsActivityCompletionsPaths,
                            value: data?.activities.numberOfNewCompletions.path
                          }
                        ]
                      : []),
                    ...(achievementsVisible
                      ? [
                          {
                            icon: 'check-circle' as IconProp,
                            label:
                              messages.analyticsActivityCompletionsAchievements,
                            value:
                              data?.activities.numberOfNewCompletions
                                .achievement
                          }
                        ]
                      : []),
                    ...(showIndividualAchievements
                      ? [
                          {
                            icon: 'star' as IconProp,
                            label: messages.individualAchievements,
                            value:
                              data?.activities.numberOfNewCompletions[
                                'individual-achievement'
                              ]
                          }
                        ]
                      : []),
                    ...(calendarEventsVisible
                      ? [
                          {
                            icon: ['far', 'calendar-days'] as IconProp,
                            value:
                              data?.activities.numberOfNewCompletions[
                                'calendar-event'
                              ],
                            label:
                              messages.analyticsActivityCompletionsCalendarEvents
                          }
                        ]
                      : []),
                    {
                      icon: 'medal',
                      label: messages.competenciesGranted,
                      value: data?.activities.numberOfNewCompletions.competency
                    }
                  ]}
                />
              </Box>
            </div>

            {!platformCompetenciesOnly && (
              <div className="stats-workload">
                <Box
                  padding={0}
                  title={messages.analyticsActivityWorkloadByType}
                  titleDescription={messages.analyticsActivityWorkloadHours}
                >
                  <StatList
                    items={[
                      ...(coursesVisible
                        ? [
                            {
                              icon: 'book' as IconProp,
                              label: messages.commonCourses,
                              value: data?.activities.workload.course
                            }
                          ]
                        : []),
                      ...(pathsVisible
                        ? [
                            {
                              icon: 'tasks' as IconProp,
                              label: messages.commonPaths,
                              value: data?.activities.workload.path
                            }
                          ]
                        : []),
                      ...(achievementsVisible
                        ? [
                            {
                              icon: 'check-circle' as IconProp,
                              label: messages.commonAchievements,
                              value: data?.activities.workload.achievement
                            }
                          ]
                        : []),
                      ...(showIndividualAchievements
                        ? [
                            {
                              icon: 'star' as IconProp,
                              label: messages.individualAchievements,
                              value:
                                data?.activities.numberOfNewCompletions[
                                  'individual-achievement'
                                ]
                            }
                          ]
                        : []),
                      ...(calendarEventsVisible
                        ? [
                            {
                              icon: ['far', 'calendar-days'] as IconProp,
                              label: messages.commonCalendarEvents,
                              value: data?.activities.workload['calendar-event']
                            }
                          ]
                        : [])
                    ]}
                  />
                </Box>
              </div>
            )}

            <div className="stats-new">
              <Box padding={0} title={messages.analyticsActivityNewAdditions}>
                <StatList
                  items={[
                    {
                      icon: 'user',
                      iconWithPlus: true,
                      label: messages.analyticsActivityAccountsCreated,
                      value: data?.activities.numberOfAccountsCreated
                    },
                    ...(coursesVisible
                      ? [
                          {
                            icon: 'book' as IconProp,
                            iconWithPlus: true,
                            label: messages.analyticsActivityCoursesPublished,
                            labelTooltip:
                              messages.analyticsActivityUserFiltersInfo,
                            value: data?.activities.numberOfCoursesPublished
                          }
                        ]
                      : []),
                    ...(pathsVisible
                      ? [
                          {
                            icon: 'tasks' as IconProp,
                            iconWithPlus: true,
                            label: messages.analyticsActivityPathsPublished,
                            labelTooltip:
                              messages.analyticsActivityUserFiltersInfo,
                            value: data?.activities.numberOfPathsPublished
                          }
                        ]
                      : []),
                    ...(achievementsVisible
                      ? [
                          {
                            icon: 'check-circle' as IconProp,
                            iconWithPlus: true,
                            label:
                              messages.analyticsActivityAchievementsCreated,
                            labelTooltip:
                              messages.analyticsActivityUserFiltersInfo,
                            value: data?.activities.numberOfAchievementsCreated
                          }
                        ]
                      : []),
                    ...(calendarEventsVisible
                      ? [
                          {
                            icon: ['far', 'calendar-days'] as IconProp,
                            iconWithPlus: true,
                            label:
                              messages.analyticsActivityCalendarEventsScheduled,
                            labelTooltip:
                              messages.analyticsActivityUserFiltersInfo,
                            value:
                              data?.activities.numberOfCalendarEventsScheduled
                          }
                        ]
                      : []),
                    {
                      icon: 'medal',
                      iconWithPlus: true,
                      label: messages.analyticsActivityCompetenciesCreated,
                      labelTooltip: messages.analyticsActivityUserFiltersInfo,
                      value: data?.activities.numberOfCompetenciesCreated
                    }
                  ]}
                />
              </Box>
            </div>
          </div>

          <div className="main">
            <div className="counts">
              <div className="count-signed-in">
                <Box>
                  <StatItemWithIcon
                    icon="user"
                    label={messages.analyticsActivitySignedInUsers}
                    value={data?.activities.numberOfSignedInUsers}
                  />
                </Box>
              </div>

              <div className="count-completions">
                <Box>
                  <StatItemWithIcon
                    icon="check"
                    label={messages.commonCompletions}
                    value={data?.activities.numberOfNewCompletions.total}
                  />
                </Box>
              </div>

              {!platformCompetenciesOnly && (
                <div className="count-workload">
                  <Box>
                    <StatItemWithIcon
                      icon="clock"
                      label={messages.commonTotalWorkloadInHours}
                      value={data?.activities.workload.total}
                    />
                  </Box>
                </div>
              )}
            </div>

            <div className="chart-completions">
              <AnalyticsBarChart
                colors={{
                  ...(coursesVisible && {
                    [messages.analyticsActivityCompletionsCourses]:
                      colorsByActivityType.course
                  }),
                  ...(pathsVisible && {
                    [messages.analyticsActivityCompletionsPaths]:
                      colorsByActivityType.path
                  }),
                  ...(achievementsVisible && {
                    [messages.analyticsActivityCompletionsAchievements]:
                      colorsByActivityType.achievement
                  }),
                  ...(showIndividualAchievements && {
                    [messages.individualAchievements]:
                      colorsByActivityType['individual-achievement']
                  }),
                  ...(calendarEventsVisible && {
                    [messages.analyticsActivityCompletionsCalendarEvents]:
                      colorsByActivityType['calendar-event']
                  }),
                  [messages.competenciesGranted]:
                    colorsByActivityType.competency
                }}
                data={data?.activities.timeSeriesData.map((item) => ({
                  xLabel: item.label,
                  ...(coursesVisible && {
                    [messages.analyticsActivityCompletionsCourses]: item.course
                  }),
                  ...(pathsVisible && {
                    [messages.analyticsActivityCompletionsPaths]: item.path
                  }),
                  ...(achievementsVisible && {
                    [messages.analyticsActivityCompletionsAchievements]:
                      item.achievement
                  }),
                  ...(showIndividualAchievements && {
                    [messages.individualAchievements]:
                      item['individual-achievement']
                  }),
                  ...(calendarEventsVisible && {
                    [messages.analyticsActivityCompletionsCalendarEvents]:
                      item['calendar-event']
                  }),
                  [messages.competenciesGranted]: item.competency
                }))}
                lineLabels={[
                  ...(coursesVisible
                    ? [messages.analyticsActivityCompletionsCourses]
                    : []),
                  ...(pathsVisible
                    ? [messages.analyticsActivityCompletionsPaths]
                    : []),
                  ...(achievementsVisible
                    ? [messages.analyticsActivityCompletionsAchievements]
                    : []),
                  ...(showIndividualAchievements
                    ? [messages.individualAchievements]
                    : []),
                  ...(calendarEventsVisible
                    ? [messages.analyticsActivityCompletionsCalendarEvents]
                    : []),
                  messages.competenciesGranted
                ]}
                stacked={true}
                title={messages.commonCompletions}
              />
            </div>

            <div className="chart-signed-in">
              <AnalyticsAreaChart
                title={messages.analyticsSignedInUsers}
                {...(isUserSuperadmin
                  ? {
                      colors: {
                        [messages.analyticsDeletedAccounts]: theme.dangerColor,
                        [messages.commonUsers]: theme.chartMainColor
                      },
                      data: data?.activities.timeSeriesData.map((item) => ({
                        xLabel: item.label,
                        [messages.analyticsDeletedAccounts]:
                          item.signedInUsersDeleted,
                        [messages.commonUsers]: item.signedInUsers
                      })),
                      areaLabels: [
                        messages.analyticsDeletedAccounts,
                        messages.commonUsers
                      ]
                    }
                  : {
                      colors: { [messages.commonUsers]: theme.chartMainColor },
                      data: data?.activities.timeSeriesData.map((item) => ({
                        xLabel: item.label,
                        [messages.commonUsers]: item.signedInUsers
                      })),
                      areaLabels: [messages.commonUsers]
                    })}
              />
            </div>
          </div>
        </div>
      )}

      <style jsx>{`
        .grid {
          display: grid;
          gap: 30px;
          grid-template-columns: 1fr;
          grid-template-areas:
            'counts'
            'chart-completions'
            'stats-completions'
            'stats-workload'
            'chart-signed-in'
            'stats-new';
        }
        .side,
        .main {
          display: contents;
        }
        .counts {
          grid-area: counts;
          display: grid;
          gap: 30px;
          grid-template-columns: 1fr;
          grid-template-areas: 'count-signed-in' 'count-completions' 'count-workload';
        }
        .count-signed-in {
          grid-area: count-signed-in;
        }
        .count-completions {
          grid-area: count-completions;
        }
        .count-workload {
          grid-area: count-workload;
        }
        .stats-completions {
          grid-area: stats-completions;
        }
        .stats-workload {
          grid-area: stats-workload;
        }
        .chart-signed-in {
          grid-area: chart-signed-in;
        }
        .chart-completions {
          grid-area: chart-completions;
        }
        @media (min-width: ${breakpoints.medium}px) {
          .grid {
            grid-template-columns: 1fr 1fr;
            grid-template-areas:
              'counts counts'
              'chart-completions chart-completions'
              'stats-completions stats-workload'
              'chart-signed-in chart-signed-in'
              'stats-new stats-new';
          }
          .counts {
            grid-template-columns: 1fr 1fr 1fr;
            grid-template-areas: 'count-signed-in count-completions count-workload';
          }
        }
        @media (min-width: ${breakpoints.huge}px) {
          .grid {
            grid-template-columns: 1fr 400px;
            grid-template-areas: 'main side';
          }
          .side {
            grid-area: side;
            display: flex;
            flex-direction: column;
            gap: 30px;
          }
          .main {
            grid-area: main;
            display: flex;
            flex-direction: column;
            gap: 30px;
          }
        }
      `}</style>
    </>
  );
};

export default AnalyticsActivity;
